import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ScrollTextIcon, ArrowLeftIcon, MailIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
interface Section {
  title: string;
  content: React.ReactNode;
}
const sections: Section[] = [{
  title: '1. Eligibility',
  content: <div className="space-y-3" data-id="element-3047">
        <p data-id="element-3048">
          You must be at least 18 years old, or have permission from a parent or
          legal guardian, to use our services.
        </p>
        <p data-id="element-3049">
          By using our services, you confirm that you are legally able to enter
          into this agreement.
        </p>
      </div>
}, {
  title: '2. Informational & Educational Purposes Only',
  content: <div className="space-y-3" data-id="element-3050">
        <p data-id="element-3051">
          Our content is provided for informational, educational, motivational,
          entertainment, and organizational purposes only.
        </p>
        <p data-id="element-3052">We do not provide:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3053">
          <li data-id="element-3054">medical advice</li>
          <li data-id="element-3055">mental health treatment</li>
          <li data-id="element-3056">therapy services</li>
          <li data-id="element-3057">legal advice</li>
          <li data-id="element-3058">financial advice</li>
          <li data-id="element-3059">professional counseling</li>
        </ul>
        <p data-id="element-3060">
          Any wellness, motivational, self-care, relationship, organizational,
          travel, or productivity content should not replace professional
          guidance from licensed professionals.
        </p>
        <p data-id="element-3061">
          Always seek appropriate professional assistance for medical, legal,
          financial, or mental health concerns.
        </p>
      </div>
}, {
  title: '3. AI-Generated Content Disclaimer',
  content: <div className="space-y-3" data-id="element-3062">
        <p data-id="element-3063">
          Some content, recommendations, visuals, scheduling tools, checklists,
          prompts, or resources may be generated or assisted by artificial
          intelligence (AI).
        </p>
        <p data-id="element-3064">
          AI-generated content may occasionally contain inaccuracies, errors,
          outdated information, or incomplete recommendations.
        </p>
        <p data-id="element-3065">
          Users are responsible for independently verifying important
          information before relying on it.
        </p>
        <p data-id="element-3066">
          We are not liable for decisions made based on AI-generated content or
          recommendations.
        </p>
      </div>
}, {
  title: '4. User Accounts',
  content: <div className="space-y-3" data-id="element-3067">
        <p data-id="element-3068">Some services may require account creation.</p>
        <p data-id="element-3069">You agree to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3070">
          <li data-id="element-3071">provide accurate information</li>
          <li data-id="element-3072">maintain the security of your account</li>
          <li data-id="element-3073">keep login credentials confidential</li>
          <li data-id="element-3074">notify us of unauthorized account activity</li>
        </ul>
        <p data-id="element-3075">
          We reserve the right to suspend or terminate accounts that violate
          these Terms.
        </p>
      </div>
}, {
  title: '5. Subscriptions, Payments & Digital Products',
  content: <div className="space-y-3" data-id="element-3076">
        <p data-id="element-3077">
          Certain features, memberships, subscriptions, courses, downloads,
          games, or digital products may require payment.
        </p>
        <p data-id="element-3078">By purchasing or subscribing, you agree that:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3079">
          <li data-id="element-3080">payment information provided is accurate</li>
          <li data-id="element-3081">subscription charges may renew automatically unless canceled</li>
          <li data-id="element-3082">pricing may change at any time</li>
          <li data-id="element-3083">
            access to paid content may be modified, updated, or discontinued
          </li>
        </ul>
        <p data-id="element-3084">
          Unless otherwise stated, digital purchases and downloadable products
          are generally non-refundable.
        </p>
        <p data-id="element-3085">Refund decisions may be made at our discretion.</p>
      </div>
}, {
  title: '6. Acceptable Use',
  content: <div className="space-y-3" data-id="element-3086">
        <p data-id="element-3087">You agree not to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3088">
          <li data-id="element-3089">misuse the services</li>
          <li data-id="element-3090">interfere with platform functionality</li>
          <li data-id="element-3091">attempt unauthorized access</li>
          <li data-id="element-3092">upload malicious software or harmful content</li>
          <li data-id="element-3093">harass, threaten, or abuse other users</li>
          <li data-id="element-3094">post illegal, hateful, or inappropriate material</li>
          <li data-id="element-3095">copy, resell, or exploit our content without permission</li>
        </ul>
        <p data-id="element-3096">
          We reserve the right to remove content or terminate access for
          violations.
        </p>
      </div>
}, {
  title: '7. Community Features & User Content',
  content: <div className="space-y-3" data-id="element-3097">
        <p data-id="element-3098">
          If community, multiplayer, social, chat, livestream, or user-submitted
          features are available, users remain responsible for their own conduct
          and submissions.
        </p>
        <p data-id="element-3099">
          By submitting content, you grant us a non-exclusive right to display,
          distribute, or use that content in connection with operating the
          platform.
        </p>
        <p data-id="element-3100">We reserve the right to remove content that violates these Terms.</p>
      </div>
}, {
  title: '8. Intellectual Property',
  content: <div className="space-y-3" data-id="element-3101">
        <p data-id="element-3102">
          All branding, logos, graphics, content, designs, downloads, app
          concepts, game concepts, checklists, visuals, text, and materials
          associated with Trippin' with Mandy and related brands are protected
          by copyright and intellectual property laws.
        </p>
        <p data-id="element-3103">You may not:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3104">
          <li data-id="element-3105">copy</li>
          <li data-id="element-3106">reproduce</li>
          <li data-id="element-3107">resell</li>
          <li data-id="element-3108">redistribute</li>
          <li data-id="element-3109">modify</li>
          <li data-id="element-3110">commercially exploit</li>
        </ul>
        <p data-id="element-3111">our content without written permission.</p>
      </div>
}, {
  title: '9. Third-Party Services',
  content: <div className="space-y-3" data-id="element-3112">
        <p data-id="element-3113">
          Our services may integrate with or link to third-party platforms,
          including payment processors, app stores, analytics providers, social
          media platforms, scheduling systems, or AI tools.
        </p>
        <p data-id="element-3114">We are not responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3115">
          <li data-id="element-3116">third-party services</li>
          <li data-id="element-3117">third-party policies</li>
          <li data-id="element-3118">outages</li>
          <li data-id="element-3119">security practices</li>
          <li data-id="element-3120">content hosted externally</li>
        </ul>
        <p data-id="element-3121">
          Use of third-party services is subject to their own terms and
          policies.
        </p>
      </div>
}, {
  title: '10. No Guarantees',
  content: <div className="space-y-3" data-id="element-3122">
        <p data-id="element-3123">We do not guarantee:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3124">
          <li data-id="element-3125">specific business results</li>
          <li data-id="element-3126">income</li>
          <li data-id="element-3127">follower growth</li>
          <li data-id="element-3128">mental health improvements</li>
          <li data-id="element-3129">productivity outcomes</li>
          <li data-id="element-3130">relationship outcomes</li>
          <li data-id="element-3131">app availability without interruption</li>
          <li data-id="element-3132">error-free operation</li>
        </ul>
        <p data-id="element-3133">Results vary by individual circumstances.</p>
      </div>
}, {
  title: '11. Limitation of Liability',
  content: <div className="space-y-3" data-id="element-3134">
        <p data-id="element-3135">
          To the fullest extent permitted by law, Trippin' with Mandy and its
          owners, affiliates, partners, employees, contractors, or
          representatives shall not be liable for:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3136">
          <li data-id="element-3137">indirect damages</li>
          <li data-id="element-3138">lost profits</li>
          <li data-id="element-3139">data loss</li>
          <li data-id="element-3140">emotional distress</li>
          <li data-id="element-3141">business interruption</li>
          <li data-id="element-3142">inaccuracies</li>
          <li data-id="element-3143">technical issues</li>
          <li data-id="element-3144">decisions made using our content or tools</li>
        </ul>
        <p data-id="element-3145">Use of our services is at your own risk.</p>
      </div>
}, {
  title: '12. Termination',
  content: <p data-id="element-3146">
        We reserve the right to suspend, restrict, or terminate access to
        services at any time for violations of these Terms or misuse of the
        platform.
      </p>
}, {
  title: '13. Changes to Terms',
  content: <div className="space-y-3" data-id="element-3147">
        <p data-id="element-3148">We may update these Terms & Conditions at any time.</p>
        <p data-id="element-3149">
          Continued use of the services after updates means you accept the
          revised Terms.
        </p>
      </div>
}, {
  title: '14. Governing Law',
  content: <p data-id="element-3150">
        These Terms shall be governed by the laws of the State of Florida,
        United States, without regard to conflict of law principles.
      </p>
}, {
  title: '15. Contact Information',
  content: <div className="space-y-3" data-id="element-3151">
        <p data-id="element-3152">For questions regarding these Terms & Conditions, please contact:</p>
        <a href="mailto:trippinwithmandy@gmail.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-blush-100 hover:bg-blush-200 text-plum-800 font-bold transition-colors" data-id="element-3153">
          <MailIcon className="w-5 h-5" data-id="element-3154" />
          trippinwithmandy@gmail.com
        </a>
      </div>
}];
export function TermsPage() {
  return <div className="min-h-screen bg-gradient-to-b from-lavender-50 via-white to-blush-50 pt-28 pb-24" data-id="element-3155">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3156">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-plum-500 hover:text-plum-700 font-bold text-sm mb-8 transition-colors" data-id="element-3157">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3158" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-3159">
          <div className="flex justify-center mb-4" data-id="element-3160">
            <GlitterHeart size={32} data-id="element-3161" />
          </div>
          <div className="w-16 h-16 rounded-2xl bg-lavender-100 flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-3162">
            <ScrollTextIcon className="w-8 h-8 text-plum-600" data-id="element-3163" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-plum-800 mb-3" data-id="element-3164">
            Terms & Conditions
          </h1>
          <p className="text-plum-500 font-medium" data-id="element-3165">For Trippin' with Mandy</p>
          <p className="text-plum-400 text-sm font-medium mt-1" data-id="element-3166">
            Last Updated: May 12, 2026
          </p>
        </motion.div>

        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-3xl p-8 shadow-sm border border-lavender-100 mb-8" data-id="element-3167">
          <p className="text-plum-700 leading-relaxed mb-4" data-id="element-3168">
            Welcome to Trippin' with Mandy, including all related brands, apps,
            websites, games, digital products, coaching content, services, and
            platforms operated by Amanda Randolph ("Company," "we," "our," or
            "us"), including but not limited to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2 mb-4" data-id="element-3169">
            <li data-id="element-3170">Trippin' Through Life</li>
            <li data-id="element-3171">Trippin' Through Life: The Game</li>
            <li data-id="element-3172">Trippin' Through Life apps and tools</li>
            <li data-id="element-3173">Trippin' with Mandy websites and social platforms</li>
            <li data-id="element-3174">Digital downloads, courses, guides, and checklists</li>
            <li data-id="element-3175">Coaching, wellness, and educational resources</li>
            <li data-id="element-3176">Community features, memberships, and subscription services</li>
          </ul>
          <p className="text-plum-700 leading-relaxed font-medium mb-3" data-id="element-3177">
            By accessing or using our websites, apps, services, products, or
            content, you agree to these Terms & Conditions.
          </p>
          <p className="text-plum-700 leading-relaxed font-medium" data-id="element-3178">
            If you do not agree to these Terms, please do not use our services.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6" data-id="element-3179">
          {sections.map((section, idx) => <motion.section key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.03
        }} className="bg-white rounded-3xl p-8 shadow-sm border border-lavender-100" data-id="element-3180">
              <h2 className="font-heading text-2xl font-bold text-plum-800 mb-4" data-id="element-3181">
                {section.title}
              </h2>
              <div className="text-plum-600 leading-relaxed space-y-3" data-id="element-3182">
                {section.content}
              </div>
            </motion.section>)}
        </div>

        {/* Footer links */}
        <div className="text-center mt-12 flex flex-wrap justify-center gap-3" data-id="element-3183">
          <Link to="/privacy" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-plum-200 text-plum-700 hover:bg-plum-50 font-bold text-sm transition-colors" data-id="element-3184">
            View Privacy Policy
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cta-500 text-white hover:bg-cta-600 font-bold text-sm transition-colors shadow-lg shadow-cta-500/20" data-id="element-3185">
            Back to Home
          </Link>
        </div>
      </div>
    </div>;
}